"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  name: "Sanne van den Berg",
  role: "Festivalbezoeker",
  company: <><span className="text-[#ffe838]">Zap</span><span className="text-[#70cf70]">GO!</span> User</>,
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/logo_techflow.png",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/avatar_sophie_de_vries.png",
  text: <>Nooit meer een lege batterij tijdens ADE! De <span className="text-[#ffe838]">Zap</span><span className="text-[#70cf70]">GO!</span> stations stonden overal en het huren ging super simpel via de app. Echt een lifesaver voor een lange dag.</>
}, {
  id: 2,
  name: "Jasper Meijer",
  role: "Eigenaar",
  company: "Café De Haven",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/logo_innovate.png",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/avatar_mark_jansen.png",
  text: <>Sinds we een <span className="text-[#ffe838]">Zap</span><span className="text-[#70cf70]">GO!</span> station in onze bar hebben, blijven gasten langer en hoeven wij geen telefoons meer achter de bar op te laden. Een top service voor onze klanten.</>
}, {
  id: 3,
  name: "Lisa Smit",
  role: "Student",
  company: <><span className="text-[#ffe838]">Zap</span><span className="text-[#70cf70]">GO!</span> User</>,
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/logo_brightfuture.png",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/avatar_lotte_bakker.png",
  text: "Ideaal voor lange dagen in de stad. Ik pik een powerbank op bij het station op het station en lever hem weer in bij de bioscoop. Super handig en betrouwbaar!"
}, {
  id: 4,
  name: "Daan de Jong",
  role: "Manager",
  company: "EventCenter",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/logo_stellar.png",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/avatar_thomas_van_dijk.png",
  text: "De extra inkomsten uit de referral fee zijn mooi meegenomen, maar de echte waarde zit in de extra service voor onze bezoekers. Geen lege accu's meer op de vloer."
}, {
  id: 5,
  name: "Emma Bakker",
  role: "Freelancer",
  company: <><span className="text-[#ffe838]">Zap</span><span className="text-[#70cf70]">GO!</span> User</>,
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/logo_creative_minds.png",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586523/img/avatar_eva_meijer.png",
  text: "Eindelijk een betrouwbare powerbank service in Nederland. De kabels zitten er al aan vast, dus geen gedoe met eigen snoertjes. Echt een moderne oplossing."
}];
export default function TestimonialsSlider() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  React.useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  return <section data-section-id="51960" id="wat-gebruikers-zeggen" ref={ref} className={cn("relative w-full py-24 overflow-hidden bg-background text-foreground transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight font-sans">
            Wat gebruikers zeggen
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Ontdek waarom duizenden gebruikers en ondernemers in Nederland kiezen voor de energie en het gemak van ZapGO!.
          </p>
        </div>

        <Carousel setApi={setApi} opts={{
        align: "center",
        loop: true
      }} className="w-full max-w-6xl mx-auto group">
          <CarouselContent className="-ml-4 md:-ml-8">
            {testimonials.map((testimonial, index) => {
            const isActive = index === current;
            return <CarouselItem key={testimonial.id} data-index={index} className="pl-4 md:pl-8 basis-full md:basis-1/2 lg:basis-1/3 py-10">
                  <div className={cn("transition-all duration-500 ease-out transform", isActive ? "scale-105 opacity-100 z-10" : "scale-95 opacity-40 hover:opacity-60")}>
                    <Card className="bg-card/40 backdrop-blur-xl border-white/10 shadow-xl h-full min-h-[320px] flex flex-col rounded-3xl overflow-hidden relative">
                      <CardContent className="p-8 flex flex-col h-full relative z-10">
                        <Quote className="w-10 h-10 text-primary mb-6 fill-primary/20" />
                        
                        <p className="text-xl text-foreground/90 font-serif leading-relaxed mb-8 flex-grow italic">
                          {testimonial.text}
                        </p>

                        <div className="flex items-center gap-4 mt-auto pt-6 border-t border-border/50">
                          <SafeHydrate><Avatar className="w-12 h-12 border-2 border-primary shadow-sm">
                            <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
                            <AvatarFallback className="bg-primary/20 text-primary">
                              {testimonial.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div>
                            <h4 className="font-bold text-foreground text-base font-sans">
                              {testimonial.name}
                            </h4>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{testimonial.role}</span>
                              <span className="w-1 h-1 rounded-full bg-primary/50" />
                              <span>{testimonial.company}</span>
                            </div>
                          </div>
                          {}
                          <img src={testimonial.logo} alt="Company Logo" className="ml-auto h-6 opacity-40 grayscale hover:grayscale-0 transition-all" />
                        </div>
                      </CardContent>
                      
                      {}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 pointer-events-none" />
                    </Card>
                  </div>
                </CarouselItem>;
          })}
          </CarouselContent>

          {}
          <div className="hidden md:block">
            <CarouselPrevious className="left-4 lg:-left-12 bg-card border-border/50 text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 shadow-lg" />
            <CarouselNext className="right-4 lg:-right-12 bg-card border-border/50 text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 shadow-lg" />
          </div>

          {}
          <div className="flex justify-center gap-3 mt-12">
            {Array.from({
            length: count
          }).map((_, i) => <Button key={i} variant="ghost" size="icon" className={cn("w-3 h-3 rounded-full p-0 transition-all duration-300 hover:bg-primary/80", i === current ? "bg-primary w-8" : "bg-muted hover:bg-muted-foreground/30")} onClick={() => api?.scrollTo(i)} aria-label={`Go to slide ${i + 1}`}>
                <span className="sr-only">Slide {i + 1}</span>
              </Button>)}
          </div>
        </Carousel>
      </div>
    </section>;
}